import {
  duckDbIdentifier,
  duckDbStringLiteral,
  duckDbValueLiteral,
} from '@cotera/client/app/etc/duckdb';
import type { DataGridCellValue } from '@cotera/client/app/components/ui/data-grid';
import type { DatasetPreviewLayer } from './types';

/** One cell the user has changed but not yet saved. */
type DatasetPendingEdit = {
  readonly rowId: string;
  readonly columnName: string;
  readonly value: DataGridCellValue;
};

/**
 * The cells edited locally and not yet persisted.
 *
 * These live outside the DuckDB table on purpose. An edit is written straight
 * into the preview table so that sorting, filtering and header stats see it —
 * but the table is rebuilt from the parquet whenever the artifact changes, a
 * dependency moves, or React remounts the page, and `CREATE OR REPLACE TABLE`
 * took every unsaved edit with it. Silently. Keeping them here means a rebuild
 * can replay them, which is what {@link createPendingEditsLayer} does.
 */
export class DatasetPendingEdits {
  /**
   * Keyed by cell, so editing the same cell ten times replays one statement
   * rather than ten, and never replays a stale intermediate value.
   */
  private readonly edits = new Map<string, DatasetPendingEdit>();

  /** Records an edit. The latest value for a cell replaces any earlier one. */
  set(edit: DatasetPendingEdit): void {
    this.edits.set(editKey(edit.rowId, edit.columnName), edit);
  }

  /** Forgets edits that have been persisted. */
  remove(removed: readonly { rowId: string; columnName: string }[]): void {
    for (const { rowId, columnName } of removed) {
      this.edits.delete(editKey(rowId, columnName));
    }
  }

  /**
   * Forgets every edit to a column that has been dropped.
   *
   * The replay layer already skips an edit whose column is gone from the schema,
   * so a stale one cannot crash a rebuild — but left in place it would resurrect
   * if a column of the same name were later re-added, writing a value the user
   * never put there. Dropping them at delete time keeps that from happening.
   */
  removeColumn(columnName: string): void {
    for (const edit of this.edits.values()) {
      if (edit.columnName === columnName) {
        this.edits.delete(editKey(edit.rowId, edit.columnName));
      }
    }
  }

  /**
   * Re-keys every edit on a column that has been renamed.
   *
   * Dropping them the way {@link removeColumn} does would be wrong here: the
   * column is still there under a new name, and the values are still the user's
   * unsaved work. The replay writes by column name, so without this they would
   * be skipped as belonging to a column the schema no longer has.
   */
  renameColumn(oldColumnName: string, newColumnName: string): void {
    for (const edit of [...this.edits.values()]) {
      if (edit.columnName !== oldColumnName) {
        continue;
      }
      this.edits.delete(editKey(edit.rowId, oldColumnName));
      this.edits.set(editKey(edit.rowId, newColumnName), {
        ...edit,
        columnName: newColumnName,
      });
    }
  }

  /**
   * Forgets every edit to rows that have been deleted.
   *
   * The same hazard `removeColumn` guards against, from the other axis: a CSV
   * dataset keys rows by row number, so an id freed by a delete can be reused
   * by a later insert, and a surviving edit would replay onto a row the user
   * never touched.
   */
  removeRows(rowIds: readonly string[]): void {
    const dropped = new Set(rowIds);
    for (const edit of this.edits.values()) {
      if (dropped.has(edit.rowId)) {
        this.edits.delete(editKey(edit.rowId, edit.columnName));
      }
    }
  }

  /**
   * Insertion-ordered, which is also apply order: a Map preserves the position
   * of a key whose value is replaced, so re-editing a cell does not reorder it
   * relative to the others.
   */
  snapshot(): readonly DatasetPendingEdit[] {
    return [...this.edits.values()];
  }
}

const editKey = (rowId: string, columnName: string): string =>
  `${rowId}\u0000${columnName}`;

/**
 * Replays unsaved cell edits onto a freshly materialized preview table.
 *
 * Sits after the operations layer, so it writes over whatever the persisted
 * log produced — which is right: a pending edit is by definition newer than
 * anything the server has.
 */
export const createPendingEditsLayer = (
  edits: DatasetPendingEdits
): DatasetPreviewLayer => ({
  id: 'dataset-pending-edits',
  mutate: ({ tableName, columns, rowIdColumn }) => {
    if (rowIdColumn === null) {
      // Nothing to address a row by; such a dataset is read-only anyway.
      return { statements: [], columns };
    }
    const present = new Set(columns.map((column) => column.name));
    return {
      statements: edits.snapshot().flatMap((edit) =>
        // A column the operation log dropped since the edit was made is gone
        // from the schema this layer inherits, and an UPDATE naming it would
        // throw and take the whole materialization with it.
        present.has(edit.columnName)
          ? [
              `UPDATE ${duckDbIdentifier(tableName)}
               SET ${duckDbIdentifier(edit.columnName)} = ${duckDbValueLiteral(
                edit.value
              )}
               WHERE CAST(${duckDbIdentifier(
                 rowIdColumn
               )} AS VARCHAR) = ${duckDbStringLiteral(edit.rowId)}`,
            ]
          : []
      ),
      columns,
    };
  },
});
